import { useState } from "react";

import * as api from "../../lib/api.js";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { signInWithApple, signInWithGoogle, signInWithReviewCode } from "../engine/signin";
import { useApp } from "../state/store";
import { Button, Field, Hint, Mark, OrDivider, StageBadge, StatusLine } from "../ui";
import { Screen } from "./screen";
import { useScreenStatus } from "./use-screen-status";

/** The sparkle on the magic-link button: the action is the extraordinary one. */
function SparkIcon() {
  return (
    <svg className="size-icon-xs flex-none" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11 3l1.7 5.3L18 10l-5.3 1.7L11 17l-1.7-5.3L4 10l5.3-1.7L11 3z" />
      <path d="M18.5 14l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z" opacity="0.7" />
    </svg>
  );
}

/** Google's G, verbatim paths -- a brand mark is not the place to improvise. */
function GoogleIcon() {
  return (
    <svg aria-hidden="true" className="size-icon flex-none" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

/**
 * Apple's logo, the path verbatim from Apple's Sign in with Apple design resources, in a
 * square box sized and placed like Google's G (#832): the G fills 22 of its 24 units, and
 * the box here leaves the apple the same margin. Monochrome, in the button's own ink.
 */
function AppleIcon() {
  return (
    <svg aria-hidden="true" className="size-icon flex-none" viewBox="6.44 6.51 26.18 26.18">
      <path
        d="M19.8196726,13.1384615 C20.902953,13.1384615 22.2608678,12.406103 23.0695137,11.4296249 C23.8018722,10.5446917 24.3358837,9.30883662 24.3358837,8.07298156 C24.3358837,7.9051494 24.3206262,7.73731723 24.2901113,7.6 C23.0847711,7.64577241 21.6353115,8.4086459 20.7656357,9.43089638 C20.0790496,10.2090273 19.4534933,11.4296249 19.4534933,12.6807374 C19.4534933,12.8638271 19.4840083,13.0469167 19.4992657,13.1079466 C19.5755531,13.1232041 19.6976128,13.1384615 19.8196726,13.1384615 Z M16.0053051,31.6 C17.4852797,31.6 18.1413509,30.6082645 19.9875048,30.6082645 C21.8641736,30.6082645 22.2761252,31.5694851 23.923932,31.5694851 C25.5412238,31.5694851 26.6245041,30.074253 27.6467546,28.6095359 C28.7910648,26.9312142 29.2640464,25.2834075 29.2945613,25.2071202 C29.1877591,25.1766052 26.0904927,23.9102352 26.0904927,20.3552448 C26.0904927,17.2732359 28.5316879,15.8848061 28.6690051,15.7780038 C27.0517133,13.4588684 24.5952606,13.3978385 23.923932,13.3978385 C22.1082931,13.3978385 20.6283185,14.4963764 19.6976128,14.4963764 C18.6906198,14.4963764 17.36322,13.4588684 15.7917006,13.4588684 C12.8012365,13.4588684 9.765,15.9305785 9.765,20.5993643 C9.765,23.4982835 10.8940528,26.565035 12.2824825,28.548506 C13.4725652,30.2268277 14.5100731,31.6 16.0053051,31.6 Z"
        fill="currentColor"
        fillRule="nonzero"
      />
    </svg>
  );
}

/**
 * The front door, as a plate rather than a form.
 *
 * Arrival, and nothing else: the mark in its milled tile, the wordmark, the one-line
 * promise, and the ways in -- each at the full 56px target, stacked, so the thumb reaches
 * both from where the thumb rests. The instrument footer closes the plate the way a
 * device's status bar would: reachability on the left, the build on the right, both in the
 * label register and neither worth a second look unless something is wrong.
 *
 * The header is hidden over it (`body[data-screen="signin"]` in `app.css`), so this `h1`
 * is the page's only one. That used to take a `front-door` class paired with
 * `[data-screen="account"]`, because one markup section served both the door and the
 * signed-in Account page; with real routes the route *is* the condition.
 */
export function SignIn({ initialCodeFor = null }: { initialCodeFor?: string | null } = {}) {
  const config = useApp((s) => s.serverConfig);
  const [status, say] = useScreenStatus();
  const [email, setEmail] = useState(initialCodeFor ?? "");
  const [sending, setSending] = useState(false);
  /**
   * The address the server asked for a sign-in code instead of mailing a link (#800): the
   * store reviewers' account, and no other. Editing the address puts the link button back,
   * because the code belongs to the address it was asked for.
   */
  const [codeFor, setCodeFor] = useState<string | null>(initialCodeFor);
  const [code, setCode] = useState("");
  const asksForCode = codeFor !== null && codeFor === email.trim();

  /**
   * Three states, not two, and the third is the one that was wrong.
   *
   * Only the buttons that can actually complete are offered -- a magic-link form on a
   * deployment with no mail provider is a dead end somebody types into. But "the server says
   * it has no sign-in" and "the server could not be asked" are different answers, and
   * `serverConfig` is null for both. `app.js` collapsed them (`methods.length > 0` with a
   * `?? []` fallback) and so told anybody whose network was down that *this deployment has no
   * sign-in configured* -- a confident wrong answer, and one the guards then contradict by
   * refusing every other screen. Boot has always attempted `/v1/config` before this screen can
   * render, so a null here means the attempt failed rather than that it has not happened.
   */
  const reachable = config !== null;
  const methods = (config?.sign_in_methods as string[] | undefined) ?? [];
  const magic = reachable && methods.includes("magic_link");
  const google = reachable && methods.includes("google");
  const apple = reachable && methods.includes("apple");
  // The providers' buttons, as one group: one divider above them, whichever are offered.
  const provider = google || apple;

  /**
   * The gate is emptiness, not shape.
   *
   * The address is still checked when the button is pressed, which can say *why* it was
   * refused -- a greyed button explains nothing, and this project would rather not have one
   * that needs to. Empty is the case that needs no explanation: there is visibly nothing in
   * the field the button acts on.
   */
  const canSend = email.trim().length > 0 && !sending;

  async function send() {
    const address = email.trim();
    if (!address.includes("@")) {
      say(t("signin.error_email"), "err");
      return;
    }
    setSending(true);
    try {
      captureAnalytics("sign_in_started", { method: "magic_link" });
      const out = await api.sendMagicLink(address);
      if (out.code_sign_in) {
        setCodeFor(address);
        setCode("");
        say(t("signin.code_prompt"), "ok");
        return;
      }
      say(
        t("signin.sent", { detail: out.detail }),
        "ok"
      );
    } catch (err) {
      captureAnalytics("sign_in_failed", { method: "magic_link", category: "request" });
      say((err as Error).message, "err");
    } finally {
      setSending(false);
    }
  }

  const canSubmitCode = code.trim().length > 0 && !sending;

  async function submitCode() {
    if (!codeFor) return;
    setSending(true);
    try {
      captureAnalytics("sign_in_started", { method: "code" });
      const outcome = await signInWithReviewCode(codeFor, code.trim());
      if (outcome.kind === "err") {
        say(outcome.message, "err");
        return;
      }
      // Signed in: the outcome router takes it from here, exactly as after a link.
      useApp.getState().setSignInOutcome(outcome);
    } finally {
      setSending(false);
    }
  }

  return (
    <Screen name="signin">
      <div className="flex flex-col items-center min-h-door justify-center text-center">
        {/* The mark, milled: a framed tile rather than a floating logo -- every other
            element on this screen sits inside a border, and the mark is not above the
            rule it enforces. */}
        <div className="size-tile-lg rounded-card border border-line bg-surface-2 flex items-center justify-center p-3">
          <Mark className="w-full h-full" />
        </div>

        {/* The brand name stays mixed case -- it is a name, not a heading. */}
        <h1 className="m-0 mt-pad font-title text-hero font-bold tracking-hero">
          {t("app.name")}
        </h1>
        <p className="m-0 mt-tight text-lede font-medium text-ink-soft">
          {t("app.tagline")}
        </p>
        {/* The app header, which carries the badge elsewhere, is hidden on this screen. */}
        <StageBadge className="mt-tight" />

        {magic || provider ? (
          <OrDivider label={t("signin.or_sign_in")} className="w-full my-hero-b" />
        ) : null}

        {magic ? (
          <div className="w-full">
            <Field
              label={t("signin.email_label")}
              hideLabel
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t("signin.email_placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {asksForCode ? (
              <>
                <Field
                  label={t("signin.code_label")}
                  hideLabel
                  type="text"
                  autoComplete="one-time-code"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={t("signin.code_placeholder")}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <Button disabled={!canSubmitCode} onClick={submitCode}>
                  {t("signin.code_submit")}
                </Button>
              </>
            ) : (
              <Button disabled={!canSend} onClick={send}>
                <span className="inline-flex items-center justify-center gap-2">
                  <SparkIcon />
                  {t("signin.send_link")}
                </span>
              </Button>
            )}
          </div>
        ) : null}

        {/* Nothing to divide *from* when only one door exists: the divider would be a line
            floating under the hero above a single button. */}
        {magic && provider ? <OrDivider /> : null}

        {/* Full width, unlike a ghost button anywhere else. The sign-in options are peers,
            and a shrink-to-fit pill under a full-width button reads as a stray control
            rather than as the other half of a choice. Apple's is Google's button with only
            the logo and the name swapped (#832), and sits above it: Apple's guidelines ask
            that Sign in with Apple be no smaller or less prominent than any other way in
            (#802). */}
        {apple ? (
          <Button variant="ghost" className="w-full" onClick={signInWithApple}>
            <span className="inline-flex items-center justify-center gap-2">
              <AppleIcon />
              {t("signin.continue_apple")}
            </span>
          </Button>
        ) : null}

        {google ? (
          <Button variant="ghost" className={apple ? "w-full mt-gutter" : "w-full"} onClick={signInWithGoogle}>
            <span className="inline-flex items-center justify-center gap-2">
              <GoogleIcon />
              {t("signin.continue_google")}
            </span>
          </Button>
        ) : null}

        {reachable && !magic && !provider ? (
          <Hint>
            {t("signin.unavailable")}
          </Hint>
        ) : null}

        {reachable ? null : (
          <Hint>
            {t("signin.offline")}
          </Hint>
        )}

      </div>

      <StatusLine kind={status.kind}>{status.message}</StatusLine>
    </Screen>
  );
}
